/**
 * @fileoverview Composes the complete SVG scene: outdoor conditions, heat
 * pump, pipe network, 3x3 room grid, roof, and the active overlay.
 *
 * @module HydraulicBalancingTesting/components/BuildingView
 */

import React from 'react';

import { SVG_DIM, COLORS, BUILDING } from '../constants';

import { RoomUnit } from './RoomUnit';
import { PipeNetwork } from './PipeNetwork';
import { HeatPumpUnit } from './HeatPumpUnit';
import {
  DeviationOverlay,
  TemperatureOverlay,
  ReturnTempOverlay,
  HeatFluxOverlay,
} from './Overlays';

const { WIDTH, HEIGHT, ROOM_WIDTH, ROOM_HEIGHT, MARGIN_LEFT, GROUND_Y } = SVG_DIM;

const BUILDING_LEFT = MARGIN_LEFT;
const BUILDING_RIGHT = MARGIN_LEFT + ROOM_WIDTH * BUILDING.COLUMNS;
const BUILDING_TOP = GROUND_Y - ROOM_HEIGHT * BUILDING.FLOORS;

/**
 * @param {Object} props
 * @param {Array<Object>} props.rooms
 * @param {Object} props.derived - engine.derived
 * @param {Object} props.params
 * @param {boolean} props.isRunning
 * @param {string} props.overlay - 'none' | 'deviation' | 'temperature' | 'return' | 'heatflux'
 * @param {number} props.selectedRoom - index 0..8
 * @param {Function} props.onSelectRoom
 * @param {boolean} props.showTargets - mission scaffold
 * @param {Function} props.isPresetEditable - (idx) => bool (for dimming)
 */
export const BuildingView = ({
  rooms,
  derived,
  params,
  isRunning,
  overlay,
  selectedRoom,
  onSelectRoom,
  showTargets,
  isPresetEditable,
  sandbox,
  onCycleRadiator,
  onCyclePreset,
}) => {
  const { network, deviations, tSupply, tReturn, cop, alarms } = derived;

  return (
    <svg width={WIDTH} height={HEIGHT} role="img" aria-label="Building hydraulic network">
      <rect width="100%" height="100%" fill="white" />

      {/* roof */}
      <polygon
        points={`${BUILDING_LEFT - 14},${BUILDING_TOP} ${BUILDING_RIGHT + 14},${BUILDING_TOP} ${(BUILDING_LEFT + BUILDING_RIGHT) / 2},${BUILDING_TOP - 52}`}
        fill="#f3f6fa"
        stroke={COLORS.WALL}
        strokeWidth={3}
      />

      <HeatPumpUnit
        isRunning={isRunning}
        hasFlow={derived.totalFlowM3h > 0.02}
        tSupply={tSupply}
        tReturn={tReturn}
        cop={cop}
        tOutdoor={params.tOutdoor}
        pumpHead={params.pumpHead}
        pumpPower={network.pumpPower}
        minFlowAlarm={alarms.minFlow && isRunning}
        supplyMode={params.supplyMode}
      />

      {/* rooms */}
      {rooms.map((room, idx) => (
        <RoomUnit
          key={room.id}
          room={room}
          roomIdx={idx}
          x={MARGIN_LEFT + ROOM_WIDTH * room.col}
          y={GROUND_Y - ROOM_HEIGHT * (room.floor + 1)}
          deviation={deviations[idx]}
          isSelected={selectedRoom === idx}
          showTargets={showTargets}
          dimmed={!isPresetEditable(idx)}
          onSelect={onSelectRoom}
          radiatorEditable={sandbox}
          presetEditable={isPresetEditable(idx)}
          onCycleRadiator={onCycleRadiator}
          onCyclePreset={onCyclePreset}
          liveFlow={network.roomFlows[idx]}
          liveVelocity={network.velocity[idx]}
        />
      ))}

      {/* pipes on top of room floors */}
      <PipeNetwork rooms={rooms} network={network} tReturn={tReturn} />

      {/* building outline */}
      <rect
        x={BUILDING_LEFT}
        y={BUILDING_TOP}
        width={ROOM_WIDTH * BUILDING.COLUMNS}
        height={ROOM_HEIGHT * BUILDING.FLOORS}
        fill="none"
        stroke={COLORS.WALL}
        strokeWidth={4}
      />

      {/* commissioning-mode banner */}
      {params.balancingMode && (
        <g>
          <rect x={BUILDING_LEFT} y={BUILDING_TOP - 28} width={250} height={22} rx={4} fill="#e6f4ff" stroke={COLORS.SECONDARY} />
          <text x={BUILDING_LEFT + 8} y={BUILDING_TOP - 12} fontSize={12} fontWeight={600} fill={COLORS.PRIMARY}>
            🔧 Commissioning mode — TRVs fully open
          </text>
        </g>
      )}

      {/* overlays */}
      {overlay === 'deviation' && <DeviationOverlay rooms={rooms} deviations={deviations} roomFlows={network.roomFlows} />}
      {overlay === 'temperature' && <TemperatureOverlay rooms={rooms} />}
      {overlay === 'return' && <ReturnTempOverlay rooms={rooms} tSupply={tSupply} />}
      {overlay === 'heatflux' && <HeatFluxOverlay rooms={rooms} />}
    </svg>
  );
};
